import * as React from 'react';
import type { KeyboardEvent } from 'react';

export type RadioOption = {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
};

export type UseRadioGroupOptions = {
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  options: RadioOption[];
};

export function useRadioGroup(opts: UseRadioGroupOptions) {
  const { name, value, defaultValue, onChange, required, disabled, options } =
    opts;

  const fallbackNameRef = React.useRef(
    `rg-${Math.random().toString(36).slice(2)}`
  );
  const groupName = name ?? fallbackNameRef.current;

  const [internal, setInternal] = React.useState<string>(defaultValue ?? '');
  const isControlled = value !== undefined;
  const selected = isControlled ? value! : internal;

  React.useEffect(() => {
    if (isControlled) return;
    if (defaultValue !== undefined) setInternal(defaultValue);
  }, [defaultValue]);

  const setSelected = React.useCallback(
    (v: string) => {
      if (!isControlled) setInternal(v);
      onChange?.(v);
    },
    [isControlled, onChange]
  );

  const enabledOptions = React.useMemo(
    () => options.filter(o => !o.disabled),
    [options]
  );

  const findIndex = React.useCallback(
    (val: string) => enabledOptions.findIndex(o => o.value === val),
    [enabledOptions]
  );

  const selectByOffset = React.useCallback(
    (offset: number) => {
      if (enabledOptions.length === 0) return;
      const idx = findIndex(selected);
      const next =
        idx === -1
          ? 0
          : (idx + offset + enabledOptions.length) % enabledOptions.length;
      setSelected(enabledOptions[next].value);
    },
    [enabledOptions, findIndex, selected, setSelected]
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Arrow navigation per WAI-ARIA Authoring Practices
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        selectByOffset(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        selectByOffset(-1);
      }
    },
    [selectByOffset]
  );

  return {
    name: groupName,
    selected,
    required: !!required,
    disabled: !!disabled,
    setSelected,
    handleKeyDown
  };
}
